import { isDuplicateAccountCode } from '../lib/errorCodes';

type ErrorPageProps = {
  error?: {
    title?: string;
    message?: string;
    details?: string;
    code?: string;
  };
  onRetry?: () => void;
};

export function ErrorPage({
  error = {
    title: 'Something went wrong',
    message: 'We encountered an unexpected error. Please try again.',
  },
  onRetry,
}: ErrorPageProps) {
  const askUserToCheckAmazon =
    error.code === 'AMAZON_CREATE_NEW_PASSWORD_REQUIRED' ||
    error.code === 'AMAZON_PASSWORD_RESET_REQUIRED';
  const isDuplicateAccount = isDuplicateAccountCode(error.code);
  return (
    <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Error Content */}
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>

          <div className="space-y-3">
            <h2 className="font-display text-2xl font-bold text-ink">
              {isDuplicateAccount ? 'Account already connected' : error.title}
            </h2>
            {isDuplicateAccount && (
              <p className="text-ink-muted max-w-sm mx-auto">
                {error.details || error.message}
              </p>
            )}
            {!askUserToCheckAmazon && !isDuplicateAccount && (
              <>
                <p className="text-ink-muted max-w-sm mx-auto">
                  {error.message}
                </p>
                {error.details && (
                  <details className="text-left">
                    <summary className="text-sm text-ink-faint cursor-pointer hover:text-ink-muted">
                      Show technical details
                    </summary>
                    <pre className="mt-2 text-xs text-ink-muted bg-paper p-3 rounded border border-rule overflow-x-auto">
                      {error.details}
                    </pre>
                  </details>
                )}
              </>
            )}
            {askUserToCheckAmazon && !isDuplicateAccount && (
              <div className="text-left">
                <span className="text-ink-muted">
                  Amazon just wants to make sure it's you before sharing your
                  e-receipts.
                  <br />
                  No worries! Sign in to{' '}
                  <a
                    href="https://www.amazon.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Amazon.com
                  </a>{' '}
                  in a new tab, then come back and try again. Bonus: you can
                  compare your purchase histories side by side!
                </span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {onRetry && (
              <button
                data-testid="error-retry-button"
                onClick={onRetry}
                className="w-full bg-primary hover:bg-primary-deep text-white px-4 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clipRule="evenodd"
                  />
                </svg>
                Try Again
              </button>
            )}
          </div>

          {/* Support */}
          <div className="text-center border-t border-rule-light pt-6">
            <p className="text-sm text-ink-muted">
              Need help?{' '}
              <a
                data-testid="error-contact-support-link"
                href="#"
                className="text-primary hover:underline"
              >
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
